/**
 * Progress Tracking Types
 * SCORM-compatible progress state for learner tracking
 * Requirements: 6.1, 6.2, 6.3, 6.4
 */

import type { CourseBlock } from './blocks';
import { blockRequiresInteraction, getBlockCompletionMode, isLessonCompleteBlock } from './blockMetadata';
import type { ExportedCourse } from './course';

/**
 * SCORM-compatible completion status
 */
export type CompletionStatus = 'not_attempted' | 'incomplete' | 'completed';

/**
 * Lesson progress status for UI rendering
 */
export type LessonProgressStatus = 'not_started' | 'in_progress' | 'completed';

/**
 * SCORM-compatible progress state
 * Requirements: 6.1, 6.2, 6.3, 6.4
 */
export interface ProgressState {
  /** Block ID -> completion status */
  blockCompletion: Record<string, boolean>;

  /** Current lesson ID (cmi.location equivalent) */
  currentLocation: string | null;

  /** Overall progress 0.0 to 1.0 (cmi.progress_measure equivalent) */
  progressMeasure: number;

  /** Completion status (cmi.completion_status equivalent) */
  completionStatus: CompletionStatus;
}

/**
 * Lesson progress summary for UI rendering
 * Requirements: 1.1, 1.2, 1.3
 */
export interface LessonProgress {
  lessonId: string;
  totalBlocks: number;
  completedBlocks: number;
  /** 0.0 to 1.0 */
  percentage: number;
  status: LessonProgressStatus;
}

/**
 * Serialized progress state for persistence
 * Requirements: 6.4, 6.5
 */
export interface SerializedProgressState {
  version: 1;
  blockCompletion: Record<string, boolean>;
  currentLocation: string | null;
  progressMeasure: number;
  completionStatus: CompletionStatus;
  timestamp: string; // ISO 8601
}

/**
 * Create a default empty progress state
 */
export function createDefaultProgressState(): ProgressState {
  return {
    blockCompletion: {},
    currentLocation: null,
    progressMeasure: 0,
    completionStatus: 'not_attempted',
  };
}

function blockContributesToProgress(block: CourseBlock): boolean {
  return getBlockCompletionMode(block) !== 'none';
}

function getProgressBlockIdsForLesson(
  course: ExportedCourse,
  blockOrder: readonly string[]
): string[] {
  return blockOrder.filter((blockId) => {
    const block = course.blocks.find((candidate) => candidate.id === blockId);
    return block ? blockContributesToProgress(block) : false;
  });
}

function getCourseProgressBlocks(course: ExportedCourse): CourseBlock[] {
  return course.blocks.filter(blockContributesToProgress);
}

/**
 * The block ID of a lesson's "Lesson Complete" block within a course.
 */
export function findLessonCompleteBlockId(
  course: ExportedCourse,
  lessonId: string
): string | null {
  const lesson = course.lessons.find((candidate) => candidate.id === lessonId);
  if (!lesson) return null;

  for (const blockId of lesson.blockOrder) {
    const block = course.blocks.find((candidate) => candidate.id === blockId);
    if (block && isLessonCompleteBlock(block)) return block.id;
  }
  return null;
}

/**
 * True when a lesson has been "reached complete" — i.e. its Lesson Complete
 * block has been reached (scrolled into view), which marks it complete.
 */
export function isLessonReachedComplete(
  course: ExportedCourse,
  lessonId: string,
  blockCompletion: Record<string, boolean>
): boolean {
  const completeBlockId = findLessonCompleteBlockId(course, lessonId);
  return completeBlockId !== null && blockCompletion[completeBlockId] === true;
}

/**
 * Calculate progress metrics from a list of block IDs and completion map
 * Requirements: 1.1, 1.2, 1.3
 */
export function calculateProgressFromBlocks(
  lessonId: string,
  blockOrder: string[],
  blockCompletion: Record<string, boolean>,
  isEngaged: boolean = false,
  lessonCompleteBlockId: string | null = null
): LessonProgress {
  const totalBlocks = blockOrder.length;
  const completedBlocks = blockOrder.filter(
    (blockId) => blockCompletion[blockId] === true
  ).length;

  const percentage = totalBlocks > 0 ? completedBlocks / totalBlocks : 1;

  // A lesson is "completed" once its Lesson Complete block is reached
  // (scrolled into view). Every lesson is expected to have one; a lesson
  // without a Lesson Complete block does not reach completion.
  const reachedComplete =
    lessonCompleteBlockId !== null && blockCompletion[lessonCompleteBlockId] === true;

  // `isEngaged` promotes a not-yet-completed lesson to `in_progress` from a
  // first-engagement signal (scroll/interaction start), decoupled from block
  // completion. It never affects the `completed` determination.
  let status: LessonProgressStatus;
  if (reachedComplete) {
    status = 'completed';
  } else if (completedBlocks > 0 || isEngaged) {
    status = 'in_progress';
  } else {
    status = 'not_started';
  }

  return {
    lessonId,
    totalBlocks,
    completedBlocks,
    percentage,
    status,
  };
}

/**
 * Calculate lesson progress from block completion state
 * Requirements: 1.1, 1.2, 1.3
 */
export function selectLessonProgress(
  course: ExportedCourse,
  lessonId: string,
  blockCompletion: Record<string, boolean>,
  isEngaged: boolean = false
): LessonProgress {
  const lesson = course.lessons.find((l) => l.id === lessonId);
  if (!lesson) {
    return {
      lessonId,
      totalBlocks: 0,
      completedBlocks: 0,
      percentage: 0,
      status: 'not_started',
    };
  }

  return calculateProgressFromBlocks(
    lessonId,
    getProgressBlockIdsForLesson(course, lesson.blockOrder),
    blockCompletion,
    isEngaged,
    findLessonCompleteBlockId(course, lessonId)
  );
}

/**
 * Calculate overall course progress measure (0.0 to 1.0)
 * Requirements: 6.2
 */
export function selectProgressMeasure(
  course: ExportedCourse,
  blockCompletion: Record<string, boolean>
): number {
  const progressBlocks = getCourseProgressBlocks(course);
  const totalBlocks = progressBlocks.length;
  if (totalBlocks === 0) {
    return 0;
  }

  const completedBlocks = progressBlocks.filter(
    (block) => blockCompletion[block.id] === true
  ).length;

  const measure = completedBlocks / totalBlocks;
  // Clamp to [0.0, 1.0] for safety
  return Math.max(0, Math.min(1, measure));
}

/**
 * Canonical ordered block IDs across the whole course: lessons in order,
 * each lesson's `blockOrder` in order. Used to map a furthest-reached
 * cursor back and forth with a percent for LMS round-tripping.
 */
export function getOrderedCourseBlockIds(course: ExportedCourse): string[] {
  const ordered: string[] = [];
  const known = new Set(course.blocks.map((b) => b.id));
  for (const lesson of course.lessons) {
    for (const id of lesson.blockOrder) {
      if (known.has(id)) ordered.push(id);
    }
  }
  return ordered;
}

/**
 * Cursor-based progress measure (0.0 to 1.0).
 *
 * Counts every block in the course (no `completionMode` filter) so the
 * percent maps directly to a position in the lesson-ordered block sequence.
 * Designed to round-trip with `percentCountWindow` for resume.
 *
 * `furthestReachedIndex === -1` means "no block reached yet" → 0.0.
 */
export function selectCursorProgressMeasure(
  course: ExportedCourse,
  furthestReachedIndex: number
): number {
  const total = getOrderedCourseBlockIds(course).length;
  if (total === 0) return 0;
  if (furthestReachedIndex < 0) return 0;
  const reached = Math.min(furthestReachedIndex + 1, total);
  return Math.max(0, Math.min(1, reached / total));
}

/** Guards float artifacts (e.g. `0.43 × 7 = 3.0100000000000002`) in the boundary math below. */
const PERCENT_BOUNDARY_EPSILON = 1e-6;

/**
 * The window of block counts consistent with a stored percent (NA-187).
 *
 * Senders floor the true position into an integer percent, so a stored `pct`
 * could have been produced by any count with `floor(100·count/total) = pct` —
 * a window of positions.
 *
 * `minCount` is provable: every true position that stores this percent is at
 * least this count, so callers may treat it as reached unconditionally.
 * `maxCount` is the forward resolution — the largest count that would still
 * have produced the stored percent through the production send expression.
 * Resolving to the maximum means resume never lands the learner behind gated
 * content they already cleared (re-watching a required video / re-answering a
 * question is the failure mode that matters); the flip side — a relaunch
 * mid-gate can land at most a block past a gate they hadn't finished — is an
 * accepted product trade-off. Positions in `[minCount, maxCount)` are the
 * genuine ambiguity zone; a percent that encodes a position exactly has
 * `minCount === maxCount`.
 */
export interface PercentCountWindow {
  minCount: number;
  maxCount: number;
}

export function percentCountWindow(
  percentMeasure: number,
  total: number
): PercentCountWindow {
  if (total <= 0 || !Number.isFinite(percentMeasure)) return { minCount: 0, maxCount: 0 };
  const clamped = Math.max(0, Math.min(1, percentMeasure));
  if (clamped >= 1) return { minCount: total, maxCount: total };
  const pct = Math.floor(clamped * 100 + PERCENT_BOUNDARY_EPSILON);
  if (pct <= 0) return { minCount: 0, maxCount: 0 };
  if (pct >= 100) return { minCount: total, maxCount: total };

  // Window minimum: the smallest count whose exact percent is `pct`.
  const windowMin = Math.max(0, Math.ceil((pct * total) / 100 - PERCENT_BOUNDARY_EPSILON));
  // Walk to the window maximum by mirroring the production send expression
  // (`Math.floor((count / total) * 100)`, floats and all): any larger count
  // that would still have stored this percent — including counts the sender
  // float under-floored into it, e.g. `0.29 × 100 = 28.999…996 → 28` — is a
  // position the learner may truly have held, so resolve forward to it.
  // Mirroring (rather than a blanket-inclusive bound) keeps the round-trip
  // EXACT for every floor-sent value: no overshoot on divisor-aligned course
  // sizes, and no percent ratchet on relaunch.
  let count = windowMin;
  while (count < total - 1 && Math.floor(((count + 1) / total) * 100) <= pct) {
    count += 1;
  }

  // Capped at total − 1: a sub-100 percent must never restore the full
  // course/lesson. A truly-complete sender always stores 100 (`total/total`
  // is exactly 1.0 in IEEE — no float path to 99), so hydration alone can
  // never complete content or fire completion side-effects.
  const maxCount = Math.min(total - 1, count);
  const minCount = Math.min(maxCount, windowMin);
  return { minCount, maxCount };
}

/**
 * True when every lesson has been reached-complete (its Lesson Complete block
 * reached) — the precondition for allowing the course-completion screen.
 *
 * Note: this is "all lessons done", NOT "the learner is on the completion
 * view." The latter is tracked separately by the learner flow and gates the
 * Assignment Panel's course-complete header.
 */
export function selectIsCourseComplete(
  course: ExportedCourse,
  blockCompletion: Record<string, boolean>
): boolean {
  if (course.lessons.length === 0) return false;
  return course.lessons.every((lesson) =>
    isLessonReachedComplete(course, lesson.id, blockCompletion)
  );
}

/**
 * Determine completion status based on block completion
 * Requirements: 6.3
 */
export function selectCompletionStatus(
  course: ExportedCourse,
  blockCompletion: Record<string, boolean>
): CompletionStatus {
  const progressBlocks = getCourseProgressBlocks(course);
  const totalBlocks = progressBlocks.length;
  if (totalBlocks === 0) {
    return 'not_attempted';
  }

  const completedBlocks = progressBlocks.filter(
    (block) => blockCompletion[block.id] === true
  ).length;

  if (completedBlocks === 0) {
    return 'not_attempted';
  } else if (completedBlocks === totalBlocks) {
    return 'completed';
  } else {
    return 'incomplete';
  }
}

/**
 * Helper to check if a block requires interaction
 */
function isRequiredBlock(block: CourseBlock): boolean {
  return blockRequiresInteraction(block);
}

/**
 * Determine if content should be blocked after a specific block
 * Requirements: 4.1, 4.4, 4.5
 */
export function selectIsContentBlocked(
  course: ExportedCourse,
  afterBlockId: string,
  blockCompletion: Record<string, boolean>
): boolean {
  const block = course.blocks.find((b) => b.id === afterBlockId);
  if (!block) {
    return false;
  }

  // Only blocks with requireInteraction can block content
  if (!isRequiredBlock(block)) {
    return false;
  }

  // Block content if the required interaction is not complete
  return blockCompletion[afterBlockId] !== true;
}

/**
 * Serialize progress state for persistence
 * Requirements: 6.4
 */
export function serializeProgressState(state: ProgressState): string {
  const serialized: SerializedProgressState = {
    version: 1,
    blockCompletion: state.blockCompletion,
    currentLocation: state.currentLocation,
    progressMeasure: state.progressMeasure,
    completionStatus: state.completionStatus,
    timestamp: new Date().toISOString(),
  };
  return JSON.stringify(serialized);
}

/**
 * Deserialize progress state from persistence
 * Requirements: 6.5, 6.6
 */
export function deserializeProgressState(json: string): ProgressState {
  try {
    const parsed = JSON.parse(json) as SerializedProgressState;

    // Validate version
    if (parsed.version !== 1) {
      return createDefaultProgressState();
    }

    // Validate required fields
    if (
      typeof parsed.blockCompletion !== 'object' ||
      parsed.blockCompletion === null
    ) {
      return createDefaultProgressState();
    }

    if (
      typeof parsed.progressMeasure !== 'number' ||
      parsed.progressMeasure < 0 ||
      parsed.progressMeasure > 1
    ) {
      return createDefaultProgressState();
    }

    const validStatuses: CompletionStatus[] = [
      'not_attempted',
      'incomplete',
      'completed',
    ];
    if (!validStatuses.includes(parsed.completionStatus)) {
      return createDefaultProgressState();
    }

    return {
      blockCompletion: parsed.blockCompletion,
      currentLocation: parsed.currentLocation,
      progressMeasure: parsed.progressMeasure,
      completionStatus: parsed.completionStatus,
    };
  } catch {
    return createDefaultProgressState();
  }
}
